import * as React from 'react';
import styled from 'styled-components';
import {transparentize} from 'polished';
import {getImageByPreview} from 'api/library';
import {getUserByID} from 'api/user/requests';
import LessonViewerContext from '../LessonViewerContext';
import Container from 'containers/Container';
import BoxContainer from 'containers/BoxContainer';
import Button, {ButtonVariant} from 'components/Button';
import {UserObject} from 'stores/UserStore';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';

const Wrapper = styled(Container)`
  gap: 30px;
  display: grid;
  margin-bottom: 40px;
`;

const Preview = styled.img`
  border-radius: 5px;
  object-fit: cover;
  max-height: 500px;
  width: 100%;
`;

const Content = styled.div`
  align-items: flex-start;
  justify-content: space-between;
  display: flex;
  
  @media screen and (max-width: 768px) {
    flex-direction: column;
  }
`;

const ContentInner = styled.div`
  max-width: 680px;
`;

const Title = styled.h1`
  margin: 0;
  
  line-height: 42px;
  font-weight: 700;
  font-size: 35px;

  @media screen and (max-width: 768px) {
    font-size: 22px;
    line-height: 28px;
  }
`;

const Description = styled.p`
  margin: 30px 0 0 0;
  line-height: 24px;
  color: ${({theme}) => `
    ${transparentize(.2, theme.variables.color)};
  `};
  @media screen and (max-width: 768px) {
    margin-top: 16px;
    font-size: 14px;
    line-height: 22px;
  }
`;

const Author = styled(BoxContainer)`
  box-sizing: border-box;
  padding: 20px;
  margin-left: 16px;
  max-width: 200px;
  width: 100%;
  gap: 4px;
  display: grid;
  text-align: center;
  justify-content: center;

  @media screen and (max-width: 768px) {
    max-width: none;
    margin-left: 0;
    margin-top: 12px;
  }
`;

const AuthorPreview = styled.img`
  margin: auto auto 10px auto;
  object-fit: cover;
  border-radius: 50%;
  height: 100px;
  width: 100px;
`;

const AuthorName = styled.span`
  line-height: 20px;
  font-weight: 500;
`;

const AuthorRole = styled.span`
  line-height: 18px;
  font-weight: 400;
  font-size: 14px;
  color: ${({theme}) => `
    ${transparentize(.6, theme.variables.color)};
  `};
`;

const AuthorControls = styled.div`
  margin-top: 10px;
`;

/**
 * This is the header component of the lesson viewer.
 * @return {React.ReactElement}
 * @constructor
 */
export default function Header(): React.ReactElement {
  const lesson = React.useContext(LessonViewerContext);
  const [
    author,
    setAuthor,
  ] = React.useState<UserObject | undefined>(undefined);

  React.useEffect(() => {
    lesson.userID && getUserByID(lesson.userID).then((response) => {
      validateResponse(response);
      if (response && !isError(response)) {
        setAuthor(response);
      }
    });
  }, []);

  /**
   * Render preview thumbnail.
   * @return {JSX.Element}
   */
  function renderPreview(): JSX.Element {
    return (
      <Preview src={getImageByPreview(lesson.preview)} />
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render title.
     * @return {JSX.Element | undefined}
     */
    function renderTitle(): JSX.Element | undefined {
      return lesson?.title ? (
        <Title>
          {lesson.title}
        </Title>
      ) : undefined;
    }

    /**
     * Render description.
     * @return {JSX.Element | undefined}
     */
    function renderDescription(): JSX.Element | undefined {
      return lesson?.description ? (
        <Description>
          {lesson.description}
        </Description>
      ) : undefined;
    }

    /**
     * Render author.
     * @return {JSX.Element | undefined}
     */
    function renderAuthor(): JSX.Element | undefined {
      return author ? (
        <Author>
          <AuthorPreview
            src={getImageByPreview(author.avatar)}
            alt="Изображение автора"
          />
          <AuthorName>
            {author.name}
          </AuthorName>
          <AuthorRole>
            Преподаватель
          </AuthorRole>
          <AuthorControls>
            <Button type="button" variant={ButtonVariant.Outline}>
              Написать
            </Button>
          </AuthorControls>
        </Author>
      ) : undefined;
    }

    return (
      <Content>
        <ContentInner>
          {renderTitle()}
          {renderDescription()}
        </ContentInner>
        {renderAuthor()}
      </Content>
    );
  }

  return (
    <Wrapper>
      {renderPreview()}
      {renderContent()}
    </Wrapper>
  );
}
